// Central Escape-dismiss stack.
//
// One arbiter for the Escape key across the whole app. Every dismissable
// surface (card inspector, popover, HUD sub-panel, themed modal…) pushes a
// dismiss callback when it opens and removes it when it closes. The app's
// single document-level Escape handler calls `dismissTop()` first: it pops
// the most-recently-opened surface and runs only that one callback, so one
// Escape closes exactly the surface that's visually on top. Never two
// nested layers at once, never the pause menu on top of an open popover.
//
// Why a stack and not N independent `keydown` listeners: with one listener
// per surface, "who owns this Escape press" is decided by listener
// registration order, not by z-order. The first-registered listener (the
// app's, bound at startup) would win over a modal mounted later. A LIFO
// stack makes ownership match the on-screen layering instead.
//
// Critical, non-dismissable surfaces (mandatory update modal, asset gate,
// connection-lost overlay) simply never register. Escape can't dismiss
// what isn't on the stack.

let entries = [];
let nextId = 1;

/**
 * Register a dismiss callback as the new topmost surface.
 * @param {() => void} dismiss invoked when this surface is the Escape target
 * @returns {number} a token to pass to {@link removeDismiss} on close/unmount
 */
export function pushDismiss(dismiss) {
  const id = nextId++;
  entries.push({ id, dismiss });
  return id;
}

/**
 * Remove a previously-registered dismiss callback. Idempotent. Safe to call
 * even if the entry was already popped by {@link dismissTop} (the common case:
 * dismissTop runs the callback, the surface unmounts, and unmount removes it
 * again).
 * @param {number} id the token returned by {@link pushDismiss}
 */
export function removeDismiss(id) {
  if (id == null) return;
  entries = entries.filter((entry) => entry.id !== id);
}

/**
 * Pop and dismiss the topmost surface, if any. The entry is removed *before*
 * its callback runs so a re-entrant dismiss can't pop it twice.
 * @returns {boolean} true if a surface was dismissed, false if the stack was empty
 */
export function dismissTop() {
  const top = entries[entries.length - 1];
  if (!top) return false;
  entries = entries.filter((entry) => entry.id !== top.id);
  try {
    top.dismiss();
  } catch (_) {
    // A surface's own teardown throwing must not wedge the Escape arbiter.
  }
  return true;
}

/** @returns {boolean} whether any dismissable surface is currently registered. */
export function hasDismissable() {
  return entries.length > 0;
}

/** Test-only: reset the module to its initial state. */
export function _resetEscStack() {
  entries = [];
  nextId = 1;
}
